// --------------------------------------------------------------------------------
//                                 General Utilities
// --------------------------------------------------------------------------------

import removeAngleBrackets from "./removeAngleBrackets";

/**
 * Checks if a URL belongs to the accuranker domain.
 * @param url - URL object to check
 * @returns `true` if the URL's hostname matches "accuranker.com" or "www.accuranker.com", `false` otherwise.
 */
const isAccurankerUrl = (url: URL) =>
  url.hostname === "accuranker.com" || url.hostname === "www.accuranker.com";

/**
 * Updates and logs changes made to the data.
 * @param path - path in the data structure where change was made
 * @param oldValue - original value before change
 * @param newValue - new value after change
 * @param updates - object tracking updates made to the data
 */
function updateAndLog(
  path: string,
  oldValue: string,
  newValue: string,
  updates: Record<string, string>
) {
  updates[path] = newValue;
  // console.log(`Updated ${path} from ${oldValue} to ${newValue}`);
}

/**
 * Processes a URL string to determine if it is relative or absolute.
 * @param value - URL string to process
 * @returns object of processed URL infom including booleans for `isRelativeUrl` and `isAbsoluteUrl`, and `url` which is the processed URL string with angle brackets removed
 */
function processUrl(value: string) {
  const angleBracketEscapedValue = removeAngleBrackets(value);
  const isRelativeUrl = angleBracketEscapedValue.startsWith("/");
  const isAbsoluteUrl = angleBracketEscapedValue.startsWith("http");

  return {
    isRelativeUrl,
    isAbsoluteUrl,
    url: angleBracketEscapedValue,
  };
}

// --------------------------------------------------------------------------------
//                            Markdown Handling Utilities
// --------------------------------------------------------------------------------

/**
 * Extracts Markdown links from a given text.
 * @param markdownText - text containing Markdown links
 * @returns array of extracted links or `null` if no links are found
 */
function extractMarkdownLinks(markdownText: string): string[] | null {
  const pattern = /\[(.*?)\]\((.*?)\)/g;
  const matches = markdownText.match(pattern);
  if (matches) {
    return matches
      .map(match => {
        const innerPattern = /\[(.*?)\]\((.*?)\)/;
        const innerMatch = match.match(innerPattern);
        if (innerMatch) {
          return innerMatch[2];
        }
        return null;
      })
      .filter(item => item !== null) as string[];
  } else {
    return null;
  }
}

/**
 * Replaces Markdown links with new link contents.
 * @param markdownText - original text containing Markdown links
 * @param newLinkContents - array of new link contents
 * @returns text with replaced Markdown links or `null` if no links were replaced
 */
function replaceMarkdownLinks(
  markdownText: string,
  newLinkContents: string[]
): string | null {
  const pattern = /\[(.*?)\]\((.*?)\)/g;
  let updatedMarkdownText = markdownText;
  let i = 0;

  updatedMarkdownText = updatedMarkdownText.replace(pattern, match => {
    if (newLinkContents[i]) {
      const updatedMatch = match.replace(
        /\((.*?)\)/,
        `(${newLinkContents[i]})`
      );
      i++;
      return updatedMatch;
    } else {
      return match;
    }
  });

  return updatedMarkdownText !== markdownText ? updatedMarkdownText : null;
}

/**
 * Updates Markdown slugs based on locale-specific slug mapping.
 * @param value - original value containing the Markdown slugs
 * @param slugs - array of original slugs
 * @param locale - locale identifier
 * @param slugToLocaleSlugMap - a mapping of original slugs to locale-specific slugs
 * @returns value with updated Markdown slugs or `null` if no slugs were updated
 */
function updateMarkdownSlug(
  value: string,
  slugs: string[],
  locale: string,
  slugToLocaleSlugMap: Record<string, Record<string, string>>
): string | null {
  const links = extractMarkdownLinks(value);
  if (!links) return null;

  const newLinks = links.map((link, index) => {
    const slug = slugs[index].split("#")[0].split("?")[0];
    if (!slug || !slugToLocaleSlugMap[slug]) return link;

    const { isAbsoluteUrl, url } = processUrl(link);
    if (isAbsoluteUrl && !isAccurankerUrl(new URL(url))) return link;
    const updatedLink = url.replace(
      new RegExp(`${slug}`),
      `${slugToLocaleSlugMap[slug][locale]}`
    );
    return updatedLink;
  });

  const updatedValue = replaceMarkdownLinks(value, newLinks);

  return updatedValue !== value ? updatedValue : null;
}

/**
 * Extracts slugs from a Markdown link.
 * @param markdownLink - string containing a Markdown link
 * @returns array of extracted slugs
 */
function extractSlugsFromMarkdownLink(markdownLink: string) {
  const matchedLinks = markdownLink.match(/\]\(<?([^>)]+)>?\)/g);
  const slugs: string[] = [];
  if (matchedLinks && matchedLinks.length > 0) {
    matchedLinks.forEach(matchedLink => {
      // Extract the URL from the match
      const urlMatch = matchedLink.match(/\]\(<?([^>)]+)>?\)/);
      let slug = urlMatch ? urlMatch[1] : null; // this should contain just the URL without the surrounding markdown syntax
      // Check if it's a URL
      if (slug && slug.startsWith("http")) {
        try {
          const url = new URL(slug);
          slug = url.pathname;
        } catch (error) {
          // Ignore invalid URLs
        }
      }
      if (slug) {
        slugs.push(slug);
      }
    });
  }
  return slugs;
}

/**
 * Handles the case where the string is Markdown text containing links.
 * Extracts slugs from the Markdown links, replaces them with locale-specific slugs, and logs the update.
 * @param value - original Markdown text
 * @param slugToLocaleSlugMap - a mapping of original slugs to locale-specific slugs
 * @param locale - locale identifier
 * @param currentPath - current traversal path within the object
 * @param updates - object tracking updates made to the data
 */
function handleMarkdownText(
  value: string,
  slugToLocaleSlugMap: Record<string, Record<string, string>>,
  locale: string,
  currentPath: string,
  updates: Record<string, string>
) {
  const slugs = extractSlugsFromMarkdownLink(value);
  if (slugs && slugs.length > 0) {
    const newValue = updateMarkdownSlug(
      value,
      slugs,
      locale,
      slugToLocaleSlugMap
    );
    if (newValue) {
      updateAndLog(currentPath, value, newValue, updates);
    }
  }
}

// --------------------------------------------------------------------------------
//                      Absolute/Relative URL Handling Utilities
// --------------------------------------------------------------------------------

/**
 * Updates the slug value based on locale-specific slug mapping.
 * @param value - original value containing the slug
 * @param slug - original slug
 * @param locale - locale identifier
 * @param slugToLocaleSlugMap - a mapping of original slugs to locale-specific slugs
 * @returns updated value with locale-specific slug or `null` if the mapping does not exist
 */
function getUpdatedSlugValue(
  value: string,
  slug: string,
  locale: string,
  slugToLocaleSlugMap: Record<string, Record<string, string>>
) {
  const angleBracketEscapedSlug = removeAngleBrackets(slug);
  const map = slugToLocaleSlugMap[angleBracketEscapedSlug];
  if (!map) return null;
  const translatedSlug = map[locale];
  if (!translatedSlug) return null;
  const updatedSlugValue = value.replace(slug, translatedSlug);
  const angleBracketEscapedUpdatedSlugValue =
    removeAngleBrackets(updatedSlugValue);
  return angleBracketEscapedUpdatedSlugValue;
}

/**
 * Handles the case where the URL is a relative path.
 * It replaces the slug part of the URL with the locale-specific slug and logs the update.
 * @param value - original URL
 * @param slugToLocaleSlugMap - a mapping of original slugs to locale-specific slugs
 * @param locale - locale identifier
 * @param path - current traversal path within the object
 * @param updates - object tracking updates made to the data
 */
function handleRelativeUrl(
  value: string,
  slugToLocaleSlugMap: Record<string, Record<string, string>>,
  locale: string,
  path: string,
  updates: Record<string, string>
) {
  const url = new URL(processUrl(value).url, "https://accuranker.com");
  const slug = url.pathname;
  const newValue = getUpdatedSlugValue(
    value,
    slug,
    locale,
    slugToLocaleSlugMap
  );

  if (newValue) {
    updateAndLog(path, value, newValue, updates);
  }
}

/**
 * Handles the case where the URL is an absolute path.
 * If the URL belongs to the accuranker domain, it replaces the slug part of the URL with the locale-specific slug and logs the update.
 * @param value - original URL
 * @param slugToLocaleSlugMap - a mapping of original slugs to locale-specific slugs
 * @param locale - locale identifier
 * @param path - current traversal path within the object
 * @param updates - object tracking updates made to the data
 */
function handleAbsoluteUrl(
  value: string,
  slugToLocaleSlugMap: Record<string, Record<string, string>>,
  locale: string,
  path: string,
  updates: Record<string, string>
) {
  try {
    const url = new URL(processUrl(value).url);
    const slug = url.pathname;

    if (isAccurankerUrl(url)) {
      const newValue = getUpdatedSlugValue(
        value,
        slug,
        locale,
        slugToLocaleSlugMap
      );
      if (newValue) {
        updateAndLog(path, value, newValue, updates);
      }
    }
  } catch (error) {
    // Ignore invalid URLs
  }
}

// --------------------------------------------------------------------------------
//                            Object Processing Utilities
// --------------------------------------------------------------------------------

/**
 * Processes a string property of the object.
 * Depending on whether the string is a relative URL, an absolute URL, or a Markdown text, different handlers are used to process it.
 * @param key - key of the property
 * @param obj - object containing the property
 * @param slugToLocaleSlugMap - a mapping of original slugs to locale-specific slugs
 * @param locale - locale identifier
 * @param path - current traversal path within the object
 * @param updates - object tracking updates made to the data
 */
function processStringProperty(
  key: string,
  obj: any,
  slugToLocaleSlugMap: Record<string, Record<string, string>>,
  locale: string,
  path: string,
  updates: Record<string, string>
) {
  const currentPath = path ? `${path}.${key}` : key;
  const value = obj[key];
  const processedUrl = processUrl(value);
  if (processedUrl.isRelativeUrl) {
    handleRelativeUrl(value, slugToLocaleSlugMap, locale, currentPath, updates);
  } else if (processedUrl.isAbsoluteUrl) {
    handleAbsoluteUrl(value, slugToLocaleSlugMap, locale, currentPath, updates);
  } else {
    handleMarkdownText(
      value,
      slugToLocaleSlugMap,
      locale,
      currentPath,
      updates
    );
  }
}

/**
 * Processes an object property by recursively calling the getSlugUpdates function on it.
 * @param key - key of the property
 * @param obj - object containing the property
 * @param slugToLocaleSlugMap - a mapping of original slugs to locale-specific slugs
 * @param locale - locale identifier
 * @param path - current traversal path within the object
 * @param updates - object tracking updates made to the data
 */
function processObjectProperty(
  key: string,
  obj: any,
  slugToLocaleSlugMap: Record<string, Record<string, string>>,
  locale: string,
  path: string,
  updates: Record<string, string>
) {
  const currentPath = path ? `${path}.${key}` : key;
  getSlugUpdates(obj[key], slugToLocaleSlugMap, locale, currentPath, updates);
}

// --------------------------------------------------------------------------------
//                                   Main function
// --------------------------------------------------------------------------------

/**
 * Traverses an object and updates slugs based on a locale-specific slug mapping.
 * The function ignores properties with certain keys (like `"id"`, `"createdAt"`, etc.)
 * For a string property, it determines whether the string is a relative URL, an absolute URL, or a Markdown text, and processes it accordingly.
 * For an object property, it recursively calls the same function.
 * Any changes made to the object are tracked in the updates parameter.
 * @param obj - object to traverse
 * @param slugToLocaleSlugMap - a mapping of original slugs to locale-specific slugs
 * @param locale - locale identifier
 * @param path - current traversal path within the object
 * @param updates - object tracking updates made to the data
 * @returns updates made to the data
 */
function getSlugUpdates(
  obj: any,
  slugToLocaleSlugMap: Record<string, Record<string, string>>,
  locale: string,
  path = "",
  updates: Record<string, string> = {}
) {
  const ignoreKeys = new Set([
    "id",
    "createdAt",
    "updatedAt",
    "publishedAt",
    "createdBy",
    "updatedBy",
    "publishedBy",
    "localizations",
    "internal",
  ]);

  for (const key in obj) {
    if (ignoreKeys.has(key)) {
      continue;
    }
    if (typeof obj[key] === "string") {
      processStringProperty(
        key,
        obj,
        slugToLocaleSlugMap,
        locale,
        path,
        updates
      );
    } else if (obj[key] && typeof obj[key] === "object") {
      processObjectProperty(
        key,
        obj,
        slugToLocaleSlugMap,
        locale,
        path,
        updates
      );
    }
  }

  return updates;
}

export default getSlugUpdates;
